import { useState } from 'react';
import { ALL_FEATURE_SETS, SHARED_PHONE_KINDS, sharedPhoneFeatureSet, sharedPhoneKindLabel, sharedPhoneKindOf, sharedPhoneTypeFits, type SharedPhoneKind } from '@stitch/pbx-adapter/catalog';
import { placesApi, type SharedPhoneView } from '../api/client';
import { Button, Card, Notice } from './ui';

const typeName = (v: string) => {
  const label = ALL_FEATURE_SETS.find((o) => o.value === v)?.label;
  return label ? `a ${label} extension` : 'another type of extension';
};

/**
 * Shared phone (1.13.0, Teampace 3067) on an extension's page: whether it's a place (lobby,
 * kitchen, conference room, classroom, hot desk) rather than a person, and which kind, for the
 * company's admins to change. A place has no Stitch sign-in, is listed apart in People, and shows as
 * a place in the receptionist console.
 *
 * The kind is Stitch's record; the phone system's extension type that gives it (Hotel room for
 * Basic, Classroom, Hot desk, Standard for Full) is a change on the page (`onType` stages it), read
 * back when it's applied. Until then the card says the phone system still has the old type.
 */
export function SharedPhoneCard({
  domain,
  extension,
  name,
  place,
  writable,
  featureSet,
  pendingFeatureSet,
  canSetType,
  onType,
  onChanged
}: {
  domain: string;
  extension: string;
  name: string;
  place: SharedPhoneView | null;
  writable: boolean;
  /** The extension's type on the phone system; undefined when the phone system doesn't show it. */
  featureSet?: string | undefined;
  /** A type change already staged on the page. */
  pendingFeatureSet?: string | undefined;
  canSetType: boolean;
  onType: (featureSet: string) => void;
  onChanged: () => void;
}) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  async function run(fn: () => Promise<unknown>) {
    setBusy(true);
    setError('');
    try {
      await fn();
      onChanged();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'That didn’t save.');
    } finally {
      setBusy(false);
    }
  }
  const known = featureSet !== undefined;
  const type = pendingFeatureSet ?? featureSet ?? '';
  const fits = !place || !known || sharedPhoneTypeFits(place.kind, type);
  const choose = (kind: SharedPhoneKind) => {
    if (!place || kind === place.kind) return;
    void run(async () => {
      await placesApi.set(domain, extension, { name: place.name, kind });
      if (known && canSetType && !sharedPhoneTypeFits(kind, type)) onType(sharedPhoneFeatureSet(kind));
    });
  };
  return (
    <Card title="Shared phone">
      <div className="card-body section-stack">
        {place ? (
          <p>
            {place.name} is a shared phone ({sharedPhoneKindLabel(place.kind)}): a place, not a person. It has no Stitch sign-in and isn’t counted as one of your people.
          </p>
        ) : (
          <p className="muted">For a lobby, kitchen, conference room or classroom phone that nobody signs in to, or a hot desk. It’s listed apart from your people and shown as a place to receptionists.</p>
        )}
        {place && (
          <div className="field">
            <span className="field-label">Kind</span>
            <div className="radio-cards compact" role="radiogroup" aria-label="Kind of shared phone">
              {SHARED_PHONE_KINDS.map((k) => (
                <label className={`radio-card${busy || !writable ? ' disabled' : ''}`} key={k.kind}>
                  <input type="radio" name={`placeKind-${extension}`} checked={place.kind === k.kind} disabled={busy || !writable} onChange={() => choose(k.kind)} />
                  <span>
                    <strong>{k.label}</strong>
                    <span className="muted"> {k.description}</span>
                  </span>
                </label>
              ))}
            </div>
          </div>
        )}
        {place && !fits && (
          <Notice tone="warn">
            On the phone system it’s still {typeName(type)}, not {sharedPhoneKindLabel(place.kind)}.{' '}
            {canSetType && writable ? (
              <Button size="small" variant="ghost" onClick={() => onType(sharedPhoneFeatureSet(place.kind))}>
                Change it on the phone system
              </Button>
            ) : (
              'Ask your provider to change it.'
            )}
          </Notice>
        )}
        {place && pendingFeatureSet !== undefined && pendingFeatureSet !== featureSet && fits && <p className="muted">Review the change below to set it on the phone system.</p>}
        {error && <Notice tone="crit">{error}</Notice>}
        <div>
          {place ? (
            <Button size="small" disabled={busy || !writable} onClick={() => void run(() => placesApi.remove(domain, extension))}>
              It’s a person’s phone
            </Button>
          ) : (
            <Button size="small" disabled={busy || !writable} onClick={() => void run(() => placesApi.set(domain, extension, { name, kind: sharedPhoneKindOf(featureSet ?? '') }))}>
              Make it a shared phone
            </Button>
          )}
        </div>
      </div>
    </Card>
  );
}
